"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  iDayByDayItineraryBlockData,
  iDayByDayItineraryDay,
  iHolidayQuoteItineraryBlock,
  type QuoteNarrativeLayoutVariant,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { formatConsistentDate } from "@/utils/timezone-date-utils";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { scrollToItineraryBlock } from "../helpers/scroll-to-itinerary-block";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import { resolveNarrativeLayout } from "../quote-narrative-layout";
import {
  ItineraryIcons,
  type ItineraryIconType,
} from "../itinerary-blocks/timeline-container";
import { cn } from "@/lib/utils";

function DayIcon({
  icon,
  className,
}: {
  icon?: ItineraryIconType | string | null;
  className?: string;
}) {
  const key = (icon && icon in ItineraryIcons
    ? icon
    : "location") as ItineraryIconType;
  const Icon = ItineraryIcons[key];
  return <Icon className={cn("h-4 w-4 shrink-0", className)} aria-hidden />;
}

interface DayByDayItinerarySectionProps {
  heading?: string;
  dayByDayItineraryData?: iDayByDayItineraryBlockData;
}

function getLinkedLabel(block: iHolidayQuoteItineraryBlock): string {
  switch (block.type) {
    case "flight":
      return `Flight ${block.flightData?.departureAirport || "?"} → ${
        block.flightData?.arrivalAirport || "?"
      }`;
    case "hotel":
      return block.accommodationData?.accommodationName || "Stay";
    case "transfer":
      return "Transfer";
    case "excursion":
      return block.excursionData?.excursionName || "Excursion";
    case "attraction":
      return block.attractionData?.attractionName || "Attraction";
    case "cruise":
      return "Cruise";
    case "car":
      return "Car hire";
    case "rail":
      return "Rail";
    default:
      return "Itinerary item";
  }
}

function DayPanel({
  day,
  linkedBlocks,
}: {
  day: iDayByDayItineraryDay;
  linkedBlocks: iHolidayQuoteItineraryBlock[];
}) {
  const body = day.body ?? "";
  const hasBody = Boolean(body && body.replace(/<[^>]*>/g, "").trim());
  const gallery = day.gallery ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
          {day.title}
        </h3>
        {day.date ? (
          <p className="mt-1 text-sm text-gray-500">
            {formatConsistentDate(day.date, "EEE, d MMM yyyy")}
          </p>
        ) : null}
        {day.summary ? (
          <p className="mt-2 text-sm sm:text-base text-gray-700">
            {day.summary}
          </p>
        ) : null}
      </div>

      {gallery.length > 0 ? <ImageGallery images={gallery} /> : null}

      {hasBody ? (
        <div
          className="prose max-w-none text-sm sm:text-base"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      ) : null}

      {linkedBlocks.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Included this day
          </p>
          <div className="flex flex-wrap gap-2">
            {linkedBlocks.map((block) => (
              <button
                key={block.id}
                type="button"
                onClick={() => scrollToItineraryBlock(block.id)}
                className="rounded-full border px-3 py-1.5 text-left text-sm transition-colors hover:bg-gray-50"
                style={{
                  borderColor: quoteThemeCssVars.primary20,
                  color: quoteThemeCssVars.primary,
                }}
              >
                <span className="capitalize">{block.type}</span>
                {" · "}
                {getLinkedLabel(block)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DayTabButton({
  day,
  active,
  onClick,
  orientation = "horizontal",
}: {
  day: iDayByDayItineraryDay;
  active: boolean;
  onClick: () => void;
  orientation?: "horizontal" | "vertical";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl px-3 py-2 text-left text-sm font-medium transition-colors",
        orientation === "horizontal" ? "shrink-0" : "w-full",
        active ? "text-white" : ""
      )}
      style={
        active
          ? quoteThemeColorStyles.primaryButton
          : {
              backgroundColor: quoteThemeCssVars.primary10,
              color: quoteThemeCssVars.primary,
            }
      }
    >
      <span className="flex items-center gap-2">
        <DayIcon icon={day.icon} className="opacity-90" />
        <span className="min-w-0">
          <span className="block text-[11px] uppercase opacity-80">
            Day {day.dayNumber}
          </span>
          <span className="line-clamp-1">{day.title}</span>
        </span>
      </span>
    </button>
  );
}

function ExpandableDay({
  day,
  linkedBlocks,
  open,
  onToggle,
}: {
  day: iDayByDayItineraryDay;
  linkedBlocks: iHolidayQuoteItineraryBlock[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        style={{ backgroundColor: open ? quoteThemeCssVars.primary10 : undefined }}
      >
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full"
            style={{
              backgroundColor: quoteThemeCssVars.primary10,
              color: quoteThemeCssVars.primary,
            }}
          >
            <DayIcon icon={day.icon} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-wide"
              style={quoteThemeColorStyles.primary}
            >
              Day {day.dayNumber}
            </p>
            <p className="font-medium text-gray-900">{day.title}</p>
          </div>
        </div>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-gray-500 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open ? (
        <div className="border-t px-4 py-4">
          <DayPanel day={day} linkedBlocks={linkedBlocks} />
        </div>
      ) : null}
    </div>
  );
}

function DayByDayLayouts({
  days,
  layout,
  blocksById,
}: {
  days: iDayByDayItineraryDay[];
  layout: QuoteNarrativeLayoutVariant;
  blocksById: Map<string, iHolidayQuoteItineraryBlock>;
}) {
  const [activeDayId, setActiveDayId] = useState(days[0]?.id);
  const [openIds, setOpenIds] = useState<Record<string, boolean>>(() =>
    days[0] ? { [days[0].id]: true } : {}
  );

  useEffect(() => {
    if (!days.some((day) => day.id === activeDayId)) {
      setActiveDayId(days[0]?.id);
    }
  }, [days, activeDayId]);

  const linkedFor = (day: iDayByDayItineraryDay) =>
    (day.linkedItineraryBlockIds ?? [])
      .map((id) => blocksById.get(id))
      .filter((block): block is iHolidayQuoteItineraryBlock => Boolean(block));

  const activeDay = days.find((day) => day.id === activeDayId) ?? days[0];

  if (layout === "accordion" || layout === "collapsible") {
    return (
      <div className="space-y-3">
        {days.map((day) => {
          const open = Boolean(openIds[day.id]);
          return (
            <ExpandableDay
              key={day.id}
              day={day}
              linkedBlocks={linkedFor(day)}
              open={open}
              onToggle={() => {
                if (layout === "accordion") {
                  setOpenIds(open ? {} : { [day.id]: true });
                } else {
                  setOpenIds((prev) => ({ ...prev, [day.id]: !prev[day.id] }));
                }
              }}
            />
          );
        })}
      </div>
    );
  }

  if (layout === "vertical_tabs") {
    return (
      <div className="grid gap-4 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
        <div className="flex gap-2 overflow-x-auto md:flex-col md:overflow-visible">
          {days.map((day) => (
            <DayTabButton
              key={day.id}
              day={day}
              active={day.id === activeDay?.id}
              orientation="vertical"
              onClick={() => setActiveDayId(day.id)}
            />
          ))}
        </div>
        {activeDay ? (
          <DayPanel day={activeDay} linkedBlocks={linkedFor(activeDay)} />
        ) : null}
      </div>
    );
  }

  // horizontal_tabs (default)
  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
        {days.map((day) => (
          <DayTabButton
            key={day.id}
            day={day}
            active={day.id === activeDay?.id}
            onClick={() => setActiveDayId(day.id)}
          />
        ))}
      </div>
      {activeDay ? (
        <DayPanel day={activeDay} linkedBlocks={linkedFor(activeDay)} />
      ) : null}
    </div>
  );
}

export function DayByDayItinerarySection({
  heading = "Itinerary",
  dayByDayItineraryData,
}: DayByDayItinerarySectionProps) {
  const { quote } = useHolidayQuoteBrand();
  const days = dayByDayItineraryData?.days ?? [];
  const sectionDomId = `trip-section-days-${days[0]?.id || "main"}`;
  const resolvedLayout = resolveNarrativeLayout(dayByDayItineraryData?.layout);
  const layout =
    resolvedLayout === "hotel_cards" ? "horizontal_tabs" : resolvedLayout;

  const blocksById = useMemo(() => {
    const map = new Map<string, iHolidayQuoteItineraryBlock>();
    for (const block of quote?.itineraryBlocks ?? []) {
      map.set(block.id, block);
    }
    return map;
  }, [quote?.itineraryBlocks]);

  if (days.length === 0) {
    return (
      <section
        id={sectionDomId}
        className="bg-white p-4 sm:p-6"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <p className="text-center text-sm text-gray-500">
          No day-by-day itinerary added
        </p>
      </section>
    );
  }

  return (
    <section
      id={sectionDomId}
      className="scroll-mt-28 bg-white p-4 sm:p-6 space-y-4 sm:space-y-5"
      style={{
        borderRadius: quoteThemeCssVars.radiusCard,
        boxShadow: quoteThemeCssVars.shadowCard,
      }}
    >
      <h2
        className="text-xl sm:text-2xl font-semibold"
        style={{
          color: quoteThemeCssVars.blockHeading,
          fontFamily: quoteThemeCssVars.headingFont,
        }}
      >
        {heading}
      </h2>

      <DayByDayLayouts
        key={layout}
        days={days}
        layout={layout}
        blocksById={blocksById}
      />
    </section>
  );
}
